import React from "react";
import _ from "lodash";

import { PageAnalyticsChart } from "./PageAnalyticsChart";
import { TrendsAreaChart } from "./charts";
import {
  useAssetTrendQuery,
  useDocumentPreviewQuery,
} from "src/query/experiences/leadsAndReports/reports";
import {
  hasPageAnalytics,
  hasVideoAnalytics,
  hasAudioAnalytics,
} from "../utils/assetAnalytics";
import styles from "./common.module.scss";

export const renderAssetTrends = (
  trend: any[],
  row: any,
  previewPages: any[] = [],
) => {
  const metadata = _.get(row, "asset.content.asset.metadata", null);
  if (!metadata) return <span className={styles.hyphen}>-</span>;

  if (hasPageAnalytics(metadata)) {
    const pageCount = Number(_.get(metadata, "pages", 0)) || 0;
    const maxIndex = trend.reduce((m: number, p: any) => Math.max(m, Number(p.index) || 0), 0);
    const bucketCount = Math.max(pageCount, previewPages.length, maxIndex, trend.length);
    if (!bucketCount) return <span className={styles.hyphen}>-</span>;

    const pages = Array.from({ length: bucketCount }, (_unused, i) => ({
      index: i + 1,
      totalTime: 0,
      count: 0,
    }));
    trend.forEach((point: any) => {
      const at = Number(point.index) - 1;
      if (at >= 0 && at < pages.length) pages[at] = { ...pages[at], ...point };
    });

    const withThumbnails = pages.map((p) => {
      const match = previewPages.find((pg: any) => pg.index === p.index - 1);
      const thumbnail = _.get(match, "background.full", null);
      return thumbnail ? { ...p, thumbnail } : p;
    });

    return <PageAnalyticsChart data={withThumbnails} valueKey="totalTime" />;
  }

  const isVideo = hasVideoAnalytics(metadata);
  const isAudio = hasAudioAnalytics(metadata);
  if (isVideo || isAudio) {
    const prefix = isVideo ? "video" : "audio";
    const segmentLength = _.get(metadata, `${prefix}_segment_length`, 1) || 1;
    const totalDuration = _.get(metadata, `${prefix}_duration`, 0);
    const totalSegments = Math.round(totalDuration) / segmentLength;
    if (!totalSegments) return <span className={styles.hyphen}>-</span>;

    let time = 0;
    const segments = Array.from({ length: totalSegments }, (_unused, i) => {
      time += segmentLength;
      const existing = trend.find((point: any) => point.index === i);
      return existing
        ? { ...existing, currentTime: time }
        : { index: i + 1, currentTime: time, totalTime: 0, count: 0 };
    });

    return <TrendsAreaChart data={segments} height={50} />;
  }

  return <span className={styles.hyphen}>-</span>;
};

export const AssetTrendCell: React.FC<{
  row: any;
  cacheKey: string;
  extraRequest?: Record<string, unknown>;
}> = ({ row, cacheKey, extraRequest }) => {
  const assetMappingId = _.get(row, "asset.content.id", null);
  const { data: trend } = useAssetTrendQuery(
    assetMappingId,
    { assetMappingId, ...extraRequest },
    cacheKey,
    Boolean(assetMappingId),
  );

  const metadata = _.get(row, "asset.content.asset.metadata", null);
  const isPageAnalytics = Boolean(metadata && hasPageAnalytics(metadata));
  const assetId = _.get(row, "asset.content.asset.id", null);
  const { data: preview } = useDocumentPreviewQuery(assetId, isPageAnalytics);
  const previewPages = _.get(preview, "pages", []);

  return <div>{renderAssetTrends(trend ?? [], row, previewPages)}</div>;
};
